/**
 * Caret Position Tracker for Plate Editor
 * 
 * Tracks the cursor position in the Plate editor for positioning
 * typing effects like confetti and other visual elements.
 */

import { PlateEditor } from '@udecode/plate/react';
import { CaretPosition } from './typing-visual-effects';

export class CaretTracker {
  private editor: PlateEditor | null = null;
  private cache = new Map<string, CaretPosition>();
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    this.setupResizeObserver();
    this.setupMouseTracking();
  }

  /**
   * Set the editor instance to track
   */
  setEditor(editor: PlateEditor): void {
    this.editor = editor;
    this.clearCache();
  }

  /**
   * Get current caret position in screen coordinates
   */
  getCaretPosition(): CaretPosition {
    try {
      const position = this.calculateCaretPosition();

      // Ensure we always return a valid position
      if (!position || position.x < 0 || position.y < 0) {
        return this.getFallbackPosition();
      }

      return position;
    } catch (error) {
      return this.getFallbackPosition();
    }
  }

  /**
   * Calculate the actual caret position
   */
  private calculateCaretPosition(): CaretPosition {
    try {
      // Method 1: Use DOM Selection API (most reliable for contenteditable)
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Get the range's bounding rectangle
        const rangeRect = range.getBoundingClientRect();
        if (rangeRect && rangeRect.height > 0) {
          return {
            x: rangeRect.left + window.scrollX,
            y: rangeRect.top + window.scrollY + rangeRect.height / 2
          };
        }

        // Fallback: Insert temporary element to get position
        const tempSpan = document.createElement('span');
        tempSpan.style.position = 'absolute';
        tempSpan.style.visibility = 'hidden';
        tempSpan.textContent = '|';

        try {
          range.insertNode(tempSpan);
          const tempRect = tempSpan.getBoundingClientRect();
          const position = {
            x: tempRect.left + window.scrollX,
            y: tempRect.top + window.scrollY + tempRect.height / 2
          };

          // Clean up
          tempSpan.remove();
          return position;
        } catch (e) {
          // Clean up on error
          if (tempSpan.parentNode) {
            tempSpan.remove();
          }
        }
      }

      // Method 2: Find focused element within Plate editor
      const focusedElement = document.activeElement;
      if (focusedElement && focusedElement.closest('[data-plate-editor]')) {
        const rect = focusedElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2 + window.scrollX,
          y: rect.top + rect.height / 2 + window.scrollY
        };
      }

      // Method 3: Use mouse position if available (last resort)
      if ((window as any).lastMouseX && (window as any).lastMouseY) {
        return {
          x: (window as any).lastMouseX,
          y: (window as any).lastMouseY
        };
      }

      return this.getFallbackPosition();
    } catch (error) {
      console.warn('Error calculating caret position:', error);
      return this.getFallbackPosition();
    }
  }

  /**
   * Get caret rectangle using Range API
   */
  private getCaretRect(range: Range): DOMRect | null {
    try {
      // Method 1: Try getBoundingClientRect on the range
      const rangeBounds = range.getBoundingClientRect();
      if (rangeBounds && rangeBounds.width >= 0 && rangeBounds.height > 0) {
        return rangeBounds;
      }

      // Method 2: Try getClientRects
      const rects = range.getClientRects();
      if (rects.length > 0) {
        return rects[0];
      }

      // Method 3: Create a temporary element at caret position
      const span = document.createElement('span');
      span.style.position = 'relative';
      span.style.display = 'inline';
      span.style.width = '0px';
      span.style.height = '1em';
      span.textContent = '\u200B'; // Zero-width space

      // Clone the range to avoid modifying the original
      const clonedRange = range.cloneRange();
      clonedRange.collapse(true); // Collapse to start
      clonedRange.insertNode(span);

      const rect = span.getBoundingClientRect();

      // Clean up
      span.parentNode?.removeChild(span);

      return rect;
    } catch (error) {
      console.warn('Error getting caret rect:', error);
      return null;
    }
  }

  /**
   * Get fallback position when caret tracking fails
   */
  private getFallbackPosition(): CaretPosition {
    try {
      // Method 1: Find the last text node in the editor
      const editorElement = document.querySelector('[data-plate-editor]') as HTMLElement;
      if (editorElement) {
        // Look for text nodes or elements with text
        const textElements = editorElement.querySelectorAll('[data-slate-leaf]');
        if (textElements.length > 0) {
          // Use the last text element as it's likely where the cursor is
          const lastTextElement = textElements[textElements.length - 1] as HTMLElement;
          const rect = lastTextElement.getBoundingClientRect();

          if (rect.width > 0 && rect.height > 0) {
            return {
              x: rect.right + window.scrollX,
              y: rect.top + window.scrollY + rect.height / 2
            };
          }
        }

        // Method 2: Use editor bounds with smart positioning
        const editorRect = editorElement.getBoundingClientRect();
        return {
          x: editorRect.left + 50 + window.scrollX, // A bit in from the left
          y: editorRect.top + 50 + window.scrollY   // A bit down from the top
        };
      }

      // Ultimate fallback - center of viewport
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      };
    } catch (error) {
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      };
    }
  }

  /**
   * Setup resize observer to clear cache on layout changes
   */
  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.clearCache();
      });

      // Observe the document body for layout changes
      this.resizeObserver.observe(document.body);
    }

    // Also clear cache on window resize
    window.addEventListener('resize', () => {
      this.clearCache();
    });
  }

  /**
   * Setup mouse tracking for fallback positioning
   */
  private setupMouseTracking(): void {
    document.addEventListener('mousemove', (e) => {
      (window as any).lastMouseX = e.clientX;
      (window as any).lastMouseY = e.clientY;
    });

    // Track clicks in the editor area
    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (target && target.closest('[data-plate-editor]')) {
        (window as any).lastMouseX = e.clientX;
        (window as any).lastMouseY = e.clientY;
      }
    });
  }

  /**
   * Clear the position cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.clearCache();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

/**
 * Hook for using caret tracking in React components
 */
export function useCaretTracker(editor: PlateEditor | null) {
  const tracker = new CaretTracker();
  
  if (editor) {
    tracker.setEditor(editor);
  }

  return {
    getCaretPosition: () => tracker.getCaretPosition(),
    clearCache: () => tracker.clearCache(),
    dispose: () => tracker.dispose()
  };
}
